import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";

const AuthContext = createContext({
  user: null, session: null, loading: true,
  signInWithGoogle: () => {}, signOut: () => {},
  showSignUpGate: false, setShowSignUpGate: () => {},
  canGenerateStory: () => true, incrementAnonCounter: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── SignUpGate state ──────────────────────────────────────────
  const [showSignUpGate, setShowSignUpGate] = useState(false);

  // Returns true if user can generate, false if blocked by gate
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const canGenerateStory = useCallback(() => {
    // RULE 1: Signed-in users ALWAYS get unlimited stories
    if (user) return true;
    // Supabase token refresh race: user is momentarily null but session exists
    const hasSupabaseSession = Object.keys(localStorage).some(key =>
      key.startsWith('sb-') && key.includes('auth-token')
    );
    if (hasSupabaseSession) return true;
    // RULE 2: Anonymous users get 5 free stories
    const count = parseInt(localStorage.getItem('you_stories_generated') || '0');
    if (count < 5) return true;
    // Edge case: gate was already skipped
    if (localStorage.getItem('you_signup_gate_passed') === 'true') return true;
    // At limit — block
    return false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user]);

  // Synchronous access token getter — single source of truth for auth headers
  const getAccessToken = useCallback(() => {
    return session?.access_token || null;
  }, [session?.access_token]);

  // Increment anonymous story counter (call BEFORE generation starts)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const incrementAnonCounter = useCallback(() => {
    if (user) return; // signed-in users don't count
    const count = parseInt(localStorage.getItem('you_stories_generated') || '0');
    localStorage.setItem('you_stories_generated', String(count + 1));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!user]);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, s) => {
      setSession(s);
      // Stabilize user reference — only update if user ID actually changed.
      // Supabase fires multiple events (INITIAL_SESSION, TOKEN_REFRESHED) with
      // new object references even when the user hasn't changed, which causes
      // infinite re-render cascades in downstream components.
      setUser(prev => {
        const incoming = s?.user ?? null;
        if (prev?.id === incoming?.id) return prev;
        return incoming;
      });
      setLoading(false);

      if (event === 'SIGNED_IN' && s?.user) {
        // Dismiss SignUpGate immediately on sign-in
        setShowSignUpGate(false);
        const u = s.user;
        const googleName = u.user_metadata?.full_name || u.user_metadata?.name || '';
        const googleAvatar = u.user_metadata?.avatar_url || '';
        localStorage.setItem('you_authenticated', 'true');
        localStorage.setItem('you_user_email', u.email);
        localStorage.setItem('you_display_name', googleName);
        localStorage.setItem('you_google_avatar', googleAvatar);

        const { data: existingPrefs } = await supabase
          .from('user_preferences').select('*').eq('user_id', u.id).maybeSingle();

        if (!existingPrefs) {
          await supabase.from('user_preferences').upsert({
            user_id: u.id,
            user_email: u.email,
            display_name: localStorage.getItem('you_display_name') || googleName,
            class_group: localStorage.getItem('you_class') || '',
            board: localStorage.getItem('you_board') || '',
            role: localStorage.getItem('you_role') || 'student',
          });
          if (localStorage.getItem('you_pet_type')) {
            await supabase.from('user_pet').upsert({
              user_id: u.id,
              user_email: u.email,
              pet_type: localStorage.getItem('you_pet_type') || 'panda',
              pet_name: localStorage.getItem('you_pet_name') || 'Buddy',
            });
          }
        } else {
          localStorage.setItem('you_display_name', existingPrefs.display_name || googleName);
          localStorage.setItem('you_class', existingPrefs.class_group || '');
          localStorage.setItem('you_board', existingPrefs.board || '');
          localStorage.setItem('you_role', existingPrefs.role || 'student');
          localStorage.setItem('you_onboarding_complete', 'true');
          if (existingPrefs.custom_avatar_url) {
            localStorage.setItem('you_custom_avatar', existingPrefs.custom_avatar_url);
          }
        }
      }

      if (event === 'SIGNED_OUT') {
        ['you_authenticated', 'you_user_email', 'you_display_name', 'you_google_avatar', 'you_custom_avatar']
          .forEach(k => localStorage.removeItem(k));
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) console.error("Google sign-in error:", error.message);
  };

  const signOut = async () => {
    // Clear local state first — even if Supabase hangs, user sees signed-out state
    localStorage.removeItem('you_authenticated');
    localStorage.removeItem('you_user_email');
    localStorage.removeItem('you_google_avatar');
    localStorage.removeItem('sb-' + process.env.REACT_APP_SUPABASE_URL?.split('//')[1]?.split('.')[0] + '-auth-token');

    // Race: Supabase signOut vs 3-second timeout
    try {
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('signOut timeout')), 3000))
      ]);
    } catch (err) {
      // signOut timeout or error — state already cleared above
    }

    // Force local state reset regardless — don't depend on onAuthStateChange firing
    setSession(null);
    setUser(null);
    setShowSignUpGate(false);
  };

  return (
    <AuthContext.Provider value={{
      user, session, loading, signInWithGoogle, signOut,
      showSignUpGate, setShowSignUpGate,
      canGenerateStory, incrementAnonCounter, getAccessToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
